<script setup lang="ts">
  import logger from '@leaflink/snitch';
  import Button from '@leaflink/stash/Button.vue';
  import CustomRender from '@leaflink/stash/CustomRender.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import { computed, isVNode, onMounted, ref } from 'vue';

  import { GlobalBanner } from '@/models/company';

  const BANNER_PRIORITIES = Object.freeze({
    LOW: 0,
    MEDIUM: 1,
    HIGH: 2,
  });

  const BANNER_COLORS = Object.freeze({
    BLUE: 'tw-bg-blue-500',
    TEAL: 'tw-bg-teal-500',
    ROYAL: 'tw-bg-royal-500',
    ORANGE: 'tw-bg-orange-500',
    GREEN: 'tw-bg-green-500',
    RED: 'tw-bg-red-500',
  });

  const props = withDefaults(defineProps<{ banners: GlobalBanner[] }>(), {
    banners: () => [],
  });

  interface Banner extends GlobalBanner {
    index: number;
    isDismissed: boolean;
  }

  const getInitialBannerState = (): Banner => ({
    canClose: true,
    link: undefined,
    to: undefined,
    callToAction: 'Click Here',
    index: 0,
    isDismissed: false,
    priority: 'medium',
    name: '',
    color: 'orange',
    message: '',
    reshow: 0,
    openInNewTab: false,
  });

  const banner = ref<Banner>(getInitialBannerState());

  /**
   * Banner will be displayed only if the following statement is true
   * - contains a message
   * - it has not been clicked on the dismissed
   */
  const isVisible = computed(() => [banner.value.message, !banner.value.isDismissed].every(Boolean));

  /**
   * Returns an array of banners sorted by the highest priority
   */
  const sortedBanners = computed(() => {
    const sorted = [...props.banners];

    return sorted.sort(
      (a, b) => BANNER_PRIORITIES[b.priority.toUpperCase()] - BANNER_PRIORITIES[a.priority.toUpperCase()],
    );
  });

  const textColor = computed(() => {
    if (!banner.value.color || ['TEAL', 'WARNING'].includes(banner.value.color.toUpperCase())) {
      return 'tw-text-ice-900';
    }

    return 'tw-text-white';
  });

  onMounted(() => {
    showBanner();
  });

  /**
   * Decides which banner should be displayed (if any)
   * depending on their priority
   */
  function showBanner() {
    let shouldShowBanner;
    let bannerExpire;

    if (sortedBanners.value.length) {
      // check that the first banner can be called
      bannerExpire = localStorage.getItem(`${sortedBanners.value[banner.value.index].name}-banner-expire-date`);

      shouldShowBanner = isBannerExpired(bannerExpire);

      if (shouldShowBanner) {
        setData(sortedBanners.value[banner.value.index]);
      } else {
        banner.value.index += 1;

        if (banner.value.index < sortedBanners.value.length) {
          // recursive call
          showBanner();
        }
      }
    }
  }

  function setData(bannerData) {
    banner.value = Object.assign({}, getInitialBannerState(), bannerData);
  }

  /**
   * Function that checks to see if banner dismissal has expired
   * gets 'banner-expire-date' from local storage
   * if it exists that means banner has been dismissed
   * checks to see if dismissal is expired
   * @param value {string} expiration value in milliseconds
   * @return {boolean} dismissal expired or not
   */
  function isBannerExpired(timestamp) {
    const now = new Date().getTime();

    if (timestamp) {
      return !(now < parseInt(timestamp));
    }

    return true;
  }

  function dismiss() {
    banner.value.isDismissed = true;
    // add `this.reshow` days to now
    const now = new Date().getTime();
    const expire = now + banner.value.reshow * 24 * 3600 * 1000;

    localStorage.setItem(`${banner.value.name}-banner-expire-date`, expire.toString());

    showBanner();
  }

  function generateBackgroundClass() {
    if (typeof banner.value.color === 'string' && BANNER_COLORS[banner.value.color.toUpperCase()]) {
      return BANNER_COLORS[banner.value.color.toUpperCase()];
    }

    return BANNER_COLORS.BLUE;
  }

  function renderContentWithRenderFn() {
    if (typeof banner.value.message !== 'function') {
      logger.error(
        `LLBanner: ${banner.value.message} is not a valid text prop value. Must be either a render function or a string.`,
      );
      return null;
    }

    const content = banner.value.message();

    // If, for some reason, the function passed it's not a render function (h)
    // and/or does not return a VNode
    if (!isVNode(content)) {
      logger.error('LLBanner: The message prop must return a VNode when passing a render function.');
      return null;
    }

    return content;
  }
</script>

<template>
  <div
    v-if="isVisible"
    class="tw-flex tw-flex-col lg:tw-flex-row tw-items-center tw-min-h-12 tw-relative tw-w-full tw-m-0 tw-py-3 tw-pl-12"
    data-test="ll-banner"
    :class="[generateBackgroundClass(), banner.canClose ? 'tw-pr-12' : 'tw-pr-3']"
  >
    <Icon class="tw-absolute tw-top-1/2 tw-left-3 -tw-mt-3" :class="textColor" name="circle-warning" />

    <span class="tw-grow tw-text-center lg:tw-px-6" :class="textColor">
      <div v-if="typeof banner.message === 'string'">{{ banner.message }}</div>
      <div v-else><CustomRender :render="renderContentWithRenderFn" /></div>
    </span>

    <div v-if="banner.callToAction" class="tw-text-right tw-mt-3 lg:tw-m-0" :class="textColor">
      <Button
        v-if="banner.link || banner.to"
        rel="noopener"
        inline
        class="tw-block hover:tw-text-white lg:tw-inline-block tw-underline"
        :class="textColor"
        :href="banner.link"
        :to="banner.to"
        :target="banner.openInNewTab ? '_blank' : '_self'"
      >
        {{ banner.callToAction }}
      </Button>
    </div>

    <button
      v-if="banner.canClose"
      class="tw-absolute tw-top-1/2 tw-right-3 -tw-mt-3 tw-cursor-pointer"
      data-test="button|dismiss-banner"
      :class="textColor"
      @click="dismiss"
    >
      <Icon name="close" />
    </button>
  </div>
</template>
